import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  getContact,
  getJackpotGames,
  getJackpotResults,
  getMarkets,
  getRates,
  getResults,
  getStarlineGames,
  getStarlineResults,
} from "../utils/api";
import { FALLBACK_RATES, JACKPOT_SLOTS, RATE_FIELDS, STARLINE_SLOTS } from "../utils/config";
import { groupJackpot, groupResults, groupStarline, normalizeMarkets, normalizeSlots } from "../utils/data";

function useRemote(loader) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    let alive = true;
    loader()
      .then((data) => alive && setState({ data, loading: false, error: null }))
      .catch((error) => alive && setState({ data: null, loading: false, error }));
    return () => {
      alive = false;
    };
  }, [loader]);
  return state;
}

export function useMarkets() {
  const { data, loading, error } = useRemote(getMarkets);
  const markets = useMemo(() => normalizeMarkets(data), [data]);
  return { markets, loading, error };
}

export function useResults() {
  const { data, loading, error } = useRemote(getResults);
  const bySlug = useMemo(() => groupResults(data && data.results), [data]);
  return { bySlug, loading, error };
}

export function useRates() {
  const { data } = useRemote(getRates);
  return useMemo(() => {
    if (!data) return FALLBACK_RATES;
    return RATE_FIELDS.map(([label, key], i) => {
      const stake = Number(data[key]);
      const win = Number(data[`${key}Value`]);
      return stake > 0 && win > 0 ? [label, `${stake}-${win}`] : FALLBACK_RATES[i];
    });
  }, [data]);
}

export function useContact() {
  const { data } = useRemote(getContact);
  return {
    whatsapp: data && data.whatsappnumber ? String(data.whatsappnumber) : "",
    mobile: data && data.mobile ? String(data.mobile) : "",
  };
}

// Re-render every minute so timings / open-closed badges stay current.
export function useNow() {
  const [now, setNow] = useState(moment());
  useEffect(() => {
    const id = setInterval(() => setNow(moment()), 60000);
    return () => clearInterval(id);
  }, []);
  return now;
}

const unwrap = (data, key) => (data && (data[key] || data.data || data.results)) || [];

// Starline: hourly single-pana slots. Falls back to the fixed slots when the list endpoint is missing.
export function useStarline() {
  const games = useRemote(getStarlineGames);
  const results = useRemote(getStarlineResults);
  const slots = useMemo(() => normalizeSlots(unwrap(games.data, "data"), STARLINE_SLOTS), [games.data]);
  const bySlug = useMemo(() => groupStarline(unwrap(results.data, "results")), [results.data]);
  const live = !!(results.data && Object.keys(bySlug).length);
  return { slots, bySlug, live, loading: games.loading || results.loading };
}

// Jackpot (Gali Disawar style): hourly slots with one result per day.
export function useJackpot() {
  const games = useRemote(getJackpotGames);
  const results = useRemote(getJackpotResults);
  const slots = useMemo(() => normalizeSlots(unwrap(games.data, "data"), JACKPOT_SLOTS), [games.data]);
  const bySlug = useMemo(() => groupJackpot(unwrap(results.data, "results")), [results.data]);
  const live = !!(results.data && Object.keys(bySlug).length);
  return { slots, bySlug, live, loading: games.loading || results.loading };
}
